/**
 * Quality enforcement store.
 * Manages quality state, warnings, and termination handling.
 */

import { create } from 'zustand';
import type { QualityCheckResponse, QualityContext } from '@/lib/api/quality';
import {
  checkSessionStart,
  checkAnswerQuality,
  checkPageQuality,
  checkCompletionQuality,
} from '@/lib/api/quality';
import { useSessionStore } from './session-store';

/**
 * Warning display state.
 */
export interface QualityWarning {
  id: string;
  message: string;
  questionCode?: string;
  timestamp: Date;
  dismissed: boolean;
}

/**
 * Quality state interface.
 */
interface QualityState {
  // Warnings
  warnings: QualityWarning[];
  activeWarning: QualityWarning | null;

  // Risk tracking
  riskFlags: string[];
  warningCount: number;
  flagCount: number;

  // Warning block state (for blocking Next until corrected)
  isBlockedByWarning: boolean;
  blockedQuestionCode: string | null;

  // Termination
  isTerminating: boolean;
  terminationReason: string | null;

  // Actions
  getQualityContext: () => QualityContext;
  checkStart: (fingerprintHash?: string) => Promise<QualityCheckResponse>;
  checkAnswer: (
    questionCode: string,
    questionType: string,
    value: unknown,
    responseTimeSeconds: number,
  ) => Promise<QualityCheckResponse>;
  checkPage: (pageNumber: number, totalTimeSeconds: number) => Promise<QualityCheckResponse>;
  checkCompletion: (totalDurationSeconds: number) => Promise<QualityCheckResponse>;
  handleQualityResponse: (response: QualityCheckResponse, questionCode?: string) => void;
  showWarning: (message: string, questionCode?: string) => void;
  dismissWarning: (id: string) => void;
  dismissActiveWarning: () => void;
  addRiskFlags: (flags: string[]) => void;
  setTerminating: (reason: string) => void;
  clearWarningBlock: (questionCode?: string) => void;
  reset: () => void;
}

let warningIdCounter = 0;

export const useQualityStore = create<QualityState>((set, get) => ({
  // Initial state
  warnings: [],
  activeWarning: null,
  riskFlags: [],
  warningCount: 0,
  flagCount: 0,
  isBlockedByWarning: false,
  blockedQuestionCode: null,
  isTerminating: false,
  terminationReason: null,

  /**
   * Build quality context from session state.
   */
  getQualityContext: (): QualityContext => {
    const session = useSessionStore.getState().session;
    return {
      surveyId: session?.surveyId,
      qualityProfileId: session?.qualityProfileId,
    };
  },

  /**
   * Check quality at session start (re-entry + IP baseline).
   */
  checkStart: async (fingerprintHash?: string): Promise<QualityCheckResponse> => {
    const sessionId = useSessionStore.getState().sessionId;
    if (!sessionId) {
      return { continue: true };
    }

    const context = get().getQualityContext();
    const response = await checkSessionStart(
      sessionId,
      {
        fingerprintHash,
        // Note: IP address is captured server-side from request headers
      },
      context,
    );

    get().handleQualityResponse(response);
    return response;
  },

  /**
   * Check quality on answer change.
   */
  checkAnswer: async (
    questionCode: string,
    questionType: string,
    value: unknown,
    responseTimeSeconds: number,
  ): Promise<QualityCheckResponse> => {
    const sessionId = useSessionStore.getState().sessionId;
    if (!sessionId) {
      return { continue: true };
    }

    const context = get().getQualityContext();
    const response = await checkAnswerQuality(
      sessionId,
      questionCode,
      questionType,
      value,
      responseTimeSeconds,
      context,
    );

    get().handleQualityResponse(response, questionCode);
    return response;
  },

  /**
   * Check quality on page submit.
   */
  checkPage: async (pageNumber: number, totalTimeSeconds: number): Promise<QualityCheckResponse> => {
    const sessionId = useSessionStore.getState().sessionId;
    if (!sessionId) {
      return { continue: true };
    }

    const context = get().getQualityContext();
    const response = await checkPageQuality(sessionId, pageNumber, totalTimeSeconds, context);
    get().handleQualityResponse(response);
    return response;
  },

  /**
   * Check quality before completion.
   */
  checkCompletion: async (totalDurationSeconds: number): Promise<QualityCheckResponse> => {
    const sessionId = useSessionStore.getState().sessionId;
    if (!sessionId) {
      return { continue: true };
    }

    const context = get().getQualityContext();
    const response = await checkCompletionQuality(sessionId, totalDurationSeconds, context);
    get().handleQualityResponse(response);
    return response;
  },

  /**
   * Handle quality check response.
   */
  handleQualityResponse: (response: QualityCheckResponse, questionCode?: string) => {
    // Add risk flags
    if (response.riskFlags?.length) {
      get().addRiskFlags(response.riskFlags);
    }

    // Handle action
    switch (response.action) {
      case 'flag':
        set((state) => ({ flagCount: state.flagCount + 1 }));
        break;

      case 'warn':
        if (response.warningMessage) {
          get().showWarning(response.warningMessage, questionCode);
        }
        // Block navigation until answer is corrected
        if (questionCode) {
          set({
            isBlockedByWarning: true,
            blockedQuestionCode: questionCode,
          });
        }
        break;

      case 'soft_terminate':
      case 'hard_terminate':
        get().setTerminating(response.reason || 'Quality check failed');
        break;
    }
  },

  /**
   * Show a warning to the respondent.
   */
  showWarning: (message: string, questionCode?: string) => {
    const warning: QualityWarning = {
      id: `warning-${++warningIdCounter}`,
      message,
      questionCode,
      timestamp: new Date(),
      dismissed: false,
    };

    set((state) => ({
      warnings: [...state.warnings, warning],
      activeWarning: warning,
      warningCount: state.warningCount + 1,
    }));

    // Auto-dismiss after 8 seconds
    setTimeout(() => {
      get().dismissWarning(warning.id);
    }, 8000);
  },

  /**
   * Dismiss a specific warning.
   */
  dismissWarning: (id: string) => {
    set((state) => {
      const warnings = state.warnings.map((w) =>
        w.id === id ? { ...w, dismissed: true } : w
      );
      const activeWarning =
        state.activeWarning?.id === id ? null : state.activeWarning;

      return { warnings, activeWarning };
    });
  },

  /**
   * Dismiss the active warning.
   */
  dismissActiveWarning: () => {
    const { activeWarning } = get();
    if (activeWarning) {
      get().dismissWarning(activeWarning.id);
    }
  },

  /**
   * Add risk flags to the session.
   */
  addRiskFlags: (flags: string[]) => {
    set((state) => {
      const existingFlags = new Set(state.riskFlags);
      flags.forEach((f) => existingFlags.add(f));
      return { riskFlags: Array.from(existingFlags) };
    });
  },

  /**
   * Set terminating state.
   */
  setTerminating: (reason: string) => {
    set({
      isTerminating: true,
      terminationReason: reason,
    });
  },

  /**
   * Clear warning block (when answer is corrected).
   * If questionCode is provided, only clear if it matches the blocked question.
   */
  clearWarningBlock: (questionCode?: string) => {
    const { blockedQuestionCode } = get();

    // If no questionCode provided, or it matches the blocked one, clear the block
    if (!questionCode || blockedQuestionCode === questionCode) {
      set({
        isBlockedByWarning: false,
        blockedQuestionCode: null,
      });
    }
  },

  /**
   * Reset store.
   */
  reset: () => {
    set({
      warnings: [],
      activeWarning: null,
      riskFlags: [],
      warningCount: 0,
      flagCount: 0,
      isBlockedByWarning: false,
      blockedQuestionCode: null,
      isTerminating: false,
      terminationReason: null,
    });
  },
}));
